import { useAuth0 } from '@auth0/auth0-react';
import { useAuthConfig } from '../providers/AuthProvider';
import { useCallback } from 'react';

/**
 * Custom hook for authentication state management
 * Provides a consistent interface for authentication across the application
 * Handles feature flag logic for progressive enhancement
 */
export const useAuth = () => {
  const isAuthEnabled = import.meta.env.VITE_AUTH_ENABLED === 'true';
  const { isConfigured } = useAuthConfig();
  
  // If auth is disabled or not configured, return mock state for backward compatibility
  if (!isAuthEnabled || !isConfigured) {
    return {
      isAuthenticated: false,
      isLoading: false,
      user: null,
      loginWithRedirect: () => Promise.resolve(),
      logout: () => {},
      getAccessTokenSilently: () => Promise.resolve(''),
      getIdTokenClaims: () => Promise.resolve(null),
      error: null,
      isAuthEnabled: isAuthEnabled && isConfigured,
    };
  }

  const {
    isAuthenticated,
    isLoading,
    user,
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
    getIdTokenClaims,
    error,
  } = useAuth0();

  return {
    isAuthenticated,
    isLoading,
    user,
    loginWithRedirect,
    logout: (options?: { logoutParams?: { returnTo?: string } }) => {
      logout({
        logoutParams: {
          returnTo: window.location.origin,
          ...options?.logoutParams,
        },
      });
    },
    getAccessTokenSilently,
    getIdTokenClaims,
    error,
    isAuthEnabled: true,
  };
};

/**
 * Hook to get access token for API calls
 * Returns null when auth is disabled for backward compatibility
 */
export const useAccessToken = () => {
  const { getAccessTokenSilently, isAuthenticated, isAuthEnabled } = useAuth();

  const getToken = useCallback(async (): Promise<string | null> => {
    if (!isAuthEnabled || !isAuthenticated) {
      return null;
    }

    try {
      return await getAccessTokenSilently();
    } catch (error) {
      console.error('Failed to get access token:', error);
      return null;
    }
  }, [getAccessTokenSilently, isAuthenticated, isAuthEnabled]);

  return { getToken };
};

/**
 * Hook to get ID token for API calls
 * Returns null when auth is disabled for backward compatibility
 */
export const useIdToken = () => {
  const { getIdTokenClaims, isAuthenticated, isAuthEnabled } = useAuth();

  const getIdToken = useCallback(async (): Promise<string | null> => {
    if (!isAuthEnabled || !isAuthenticated) {
      return null;
    }

    try {
      const claims = await getIdTokenClaims();
      return claims?.__raw || null;
    } catch (error) {
      console.error('Failed to get ID token:', error);
      return null;
    }
  }, [getIdTokenClaims, isAuthenticated, isAuthEnabled]);

  return { getIdToken };
};

/**
 * Combined hook for getting both access and ID tokens
 * Useful for API calls that need both tokens
 */
export const useTokens = () => {
  const { getToken: getAccessToken } = useAccessToken();
  const { getIdToken } = useIdToken();

  const getTokens = useCallback(async (): Promise<{
    accessToken: string | null;
    idToken: string | null;
  }> => {
    const [accessToken, idToken] = await Promise.all([
      getAccessToken(),
      getIdToken()
    ]);
    
    return { accessToken, idToken };
  }, [getAccessToken, getIdToken]);

  return { getTokens };
};